import React, { useState, useEffect, useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { Text, Money, Button, Spinner } from '@belong/ui';
import { ApiClientContext } from 'common/contexts/api-client';
import ModalV2 from 'components/Modal/ModalV2/ModalV2';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMethodSelectorContainer from 'containers/PaymentMethodSelectorContainer/PaymentMethodSelectorContainer';
import { useFastMaintenanceSettings } from 'hooks/useFastMaintenanceSettings';
import ModalLayout from 'layouts/ModalLayout/ModalLayout';
import { PAY_NOW_STRINGS } from 'pages/Accounts/Pages/YourHome/Rent/PayNow/pay-now-strings';
import {
  getBalanceIds,
  getDefaultPaymentMethod,
  getMatchingFee,
  shouldShowFee,
  getFeeLabel,
} from 'pages/Accounts/Pages/YourHome/Rent/PayNow/pay-now-utils';
import {
  getBalanceSubtitle,
  getBalanceSubtitleStyles,
} from 'pages/Accounts/utils/get-balance-subtitle';
import PropTypes from 'prop-types';
import {
  simulateSplitPayments,
  createBillsPayment,
} from 'store/redux/accounts/actions';
import { selectSimulatePayments } from 'store/redux/accounts/selectors';
import { fetchPaymentMethods } from 'store/redux/user/actions';
import { selectPaymentMethods } from 'store/redux/user/selectors';
import { formatString } from 'strings';
import { ACCOUNTS_EARNINGS } from 'strings/accounts/earnings';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import SplitPaymentModal from '../SplitPaymentModal/SplitPaymentModal';
import { SplitPaymentsBanner } from '../SplitPaymentsBanner/SplitPaymentsBanner';

function FeeRow({ label, value }) {
  return (
    <tr>
      <th>
        <div className="text-left">
          <Text className="mt-xs">{label}</Text>
        </div>
      </th>
      <td>
        <div className="text-right">
          <Money format="DOLLARS_WITH_CENTS" value={value} />
        </div>
      </td>
    </tr>
  );
}

FeeRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
};

function BalanceDetailsModal({
  balance: selectedBalance,
  onHide,
  standalone = false,
  handleShowFinanceSuccessModal,
  simulateSplitPayments: simulateSplitPaymentsAction,
  isResident = false,
  onPaymentComplete,
  showPayNow = false,
  paymentMethods: paymentMethodsRaw,
  fetchPaymentMethods: fetchPaymentMethodsAction,
  createBillsPayment: createBillsPaymentAction,
  ...rest
}) {
  const apiClient = useContext(ApiClientContext);
  const { isEnabled: isFastMaintenanceEnabled } = useFastMaintenanceSettings();

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  const [splitPaymentModal, setSplitPaymentModal] = useState(false);
  const [maxCashBack, setMaxCashBack] = useState(0);
  const balance = selectedBalance?.balance;
  const home = selectedBalance?.address;
  const isAutosplittable = selectedBalance?.autosplittable;

  // Mirror the list's per-line-item status (e.g. "Failed On 06/01"). Use the
  // item's own fields, only borrowing the parent line item's billStatusInfo
  // when the item lacks one — so a child's category/dueOn drive its subtitle
  // rather than the parent's. Shown only when fast maintenance is enabled,
  // like in the earnings list.
  function renderLineItemStatus(item) {
    if (!isFastMaintenanceEnabled) return null;
    const source = item?.billStatusInfo
      ? item
      : { ...item, billStatusInfo: balance?.billStatusInfo };
    const subtitle = getBalanceSubtitle(source);
    if (!subtitle) return null;
    return (
      <Text
        variant="p1"
        className={`text-left ${getBalanceSubtitleStyles(
          source?.billStatusInfo,
          source?.isDelayed,
        )}`}
      >
        {subtitle}
      </Text>
    );
  }

  // Pay now flow state
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [billTotal, setBillTotal] = useState(null);
  const [billFees, setBillFees] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);

  const showPlanIt = Boolean(
    balance?.isFinanceable && selectedBalance?.billingInfo,
  );

  const canPayNow =
    showPayNow && balance?.canBePaid && balance?.category === 'Expenses';

  const showSplitItBar = !isResident && showPlanIt;

  const paymentMethods = useMemo(
    () => (paymentMethodsRaw ?? []).filter((pm) => pm.isVerified),
    [paymentMethodsRaw],
  );

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((pm) => pm.accountId === selectedPaymentMethodId),
    [paymentMethods, selectedPaymentMethodId],
  );

  const balanceIdsForApi = useMemo(
    () => (balance ? getBalanceIds([balance]) : []),
    [balance],
  );

  const matchingFee = useMemo(() => {
    if (!billFees || !selectedPaymentMethod) return undefined;
    return getMatchingFee(billFees, selectedPaymentMethod);
  }, [billFees, selectedPaymentMethod]);

  const showFee = selectedPaymentMethod
    ? shouldShowFee(selectedPaymentMethod, matchingFee)
    : false;

  const feeAmount = showFee && matchingFee ? matchingFee.feeAmount : 0;

  // Reset pay now flow state when modal closes
  useEffect(() => {
    if (!rest.show) {
      setSelectedPaymentMethodId(null);
      setBillTotal(null);
      setBillFees(null);
      setHasApiError(false);
    }
  }, [rest.show]);

  // Fetch payment methods when modal opens with a payable balance
  useEffect(() => {
    if (!canPayNow || !rest.show) return;
    if (!paymentMethodsRaw || paymentMethodsRaw.length === 0) {
      fetchPaymentMethodsAction();
    }
  }, [canPayNow, rest.show]);

  // Fetch bill total and fees when modal opens with a payable balance
  useEffect(() => {
    if (!canPayNow || !rest.show || balanceIdsForApi.length === 0) return;

    let cancelled = false;
    setIsCalculating(true);
    setHasApiError(false);

    async function fetchTotalAndFees() {
      try {
        const result = await apiClient.post('/users/bills/total', {
          balances: balanceIdsForApi,
        });
        if (cancelled) return;
        setBillTotal(result.amount);

        if (result.amount > 0) {
          const feesResult = await apiClient.get(
            `/users/bills/fees?amount=${result.amount}`,
          );
          if (cancelled) return;
          setBillFees(feesResult?.fees ?? null);
        }
      } catch {
        if (!cancelled) setHasApiError(true);
      } finally {
        if (!cancelled) setIsCalculating(false);
      }
    }

    fetchTotalAndFees();
    return () => {
      cancelled = true;
    };
  }, [canPayNow, rest.show, apiClient, balanceIdsForApi]);

  // Auto-select default payment method when payment methods load
  useEffect(() => {
    if (
      canPayNow &&
      rest.show &&
      paymentMethods.length > 0 &&
      !selectedPaymentMethodId
    ) {
      const defaultMethod = getDefaultPaymentMethod(
        paymentMethods,
        false,
        false,
      );
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.accountId);
      }
    }
  }, [canPayNow, rest.show, paymentMethods, selectedPaymentMethodId]);

  function handleHide() {
    if (onHide) onHide();
  }

  function handleSplitPaymentsModal() {
    setSplitPaymentModal(false);
  }

  function navigateToSplitPayments() {
    setSplitPaymentModal(true);
  }

  function onShowFinanceSuccessModal() {
    handleHide();
    handleShowFinanceSuccessModal();
  }

  async function handlePayNow() {
    if (!selectedPaymentMethod?.accountId) return;
    setIsSubmitting(true);
    try {
      await createBillsPaymentAction({
        paymentAccountUniqueId: selectedPaymentMethod.accountId,
        balances: balanceIdsForApi,
      });
      if (onPaymentComplete) onPaymentComplete({ success: true });
    } catch (error) {
      const dataArr = error?.data;
      const message =
        dataArr?.[0]?.message ??
        error?.message ??
        PAY_NOW_STRINGS['payment.error'];
      if (onPaymentComplete)
        onPaymentComplete({ success: false, errorMessage: message });
    } finally {
      setIsSubmitting(false);
    }
  }

  const isPayNowDisabled =
    !selectedPaymentMethod || isCalculating || hasApiError;

  useEffect(() => {
    const doSimulation = async () => {
      const buildSimulation = {
        installments: 12,
        balanceUniqueIds: balance.balances.flat().map((bal) => bal.sourceKey),
      };
      try {
        const simulationResponse =
          await simulateSplitPaymentsAction(buildSimulation);
        setMaxCashBack(simulationResponse.maxCashBack);
      } catch (err) {
        console.error(err);
      }
    };

    if (
      typeof balance !== 'undefined' &&
      typeof balance.balances !== 'undefined' &&
      rest.show
    )
      doSimulation();
  }, [balance]);

  if (!rest.show && typeof balance === 'undefined') {
    return null;
  }

  const payNowFlowSection =
    canPayNow &&
    (isSubmitting ? (
      <div className="flex items-center justify-center py-2xl">
        <Spinner />
      </div>
    ) : (
      <div className="mb-xl mt-xl md:px-sm">
        {hasApiError && (
          <div className="mb-sm rounded-small bg-red-translucent-light p-sm">
            <Text className="text-red">{PAY_NOW_STRINGS['payment.error']}</Text>
          </div>
        )}
        {isCalculating && (
          <div className="flex items-center justify-center py-md">
            <Spinner />
          </div>
        )}
        <Text fontWeight="semibold" className="mb-sm">
          {PAY_NOW_STRINGS['payment_methods.title']}
        </Text>
        <PaymentMethodSelectorContainer
          needsPaymentMethod
          selectedPaymentMethodId={selectedPaymentMethodId ?? ''}
          onSelectPaymentMethod={(id) => setSelectedPaymentMethodId(id)}
          showOnlyValidPaymentMethods
          paymentModalProps={paymentModalProps}
          hasFee
        />
      </div>
    ));

  const showCreditCardFee =
    showFee && matchingFee && selectedPaymentMethod && billTotal != null;

  // The fee and total magnitudes come straight from the back-end (`feeAmount`
  // and `matchingFee.totalWithFee`, both computed on the absolute `billTotal`),
  // so they always match what will actually be charged. They arrive unsigned,
  // so we only borrow the sign from the line item shown (netBalance / remaining)
  // so the fee and total display the same sign as the charge they belong to.
  const signOf = (baseTotal) => (baseTotal < 0 ? -1 : 1);
  const getSignedFee = (baseTotal) => signOf(baseTotal) * feeAmount;
  const getTotalWithFee = (baseTotal) =>
    showCreditCardFee
      ? signOf(baseTotal) * matchingFee.totalWithFee
      : baseTotal;

  if (standalone) {
    const standaloneBalances =
      balance.sourceType === 'GroupKey' ? balance.balances : [balance];
    const { remaining } = balance;

    return (
      <>
        <ModalV2
          {...rest}
          show={rest.show}
          noPadding
          showSpinner={false}
          onHide={handleHide}
          className="p-0"
        >
          <ModalLayout fixedBottom className="pt-lg">
            <ModalLayout.Content className="p-0">
              <ModalLayout.Section>
                <ModalLayout.Title className="md:px-sm">
                  {balance?.description}
                </ModalLayout.Title>
                <ModalLayout.SubTitle noPadding>
                  <Text className="text-navy">{formatString(home)}</Text>
                </ModalLayout.SubTitle>
                <div className={`mt-xl ${canPayNow ? '' : 'pb-xl'}`}>
                  <div className="flex md:px-sm">
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th>
                            <div className="text-left">
                              <Text variant="h3" fontWeight="semibold">
                                Details
                              </Text>
                            </div>
                          </th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {standaloneBalances?.map((item) => (
                          <React.Fragment key={item.id}>
                            <tr className="h-sm" colSpan="2" key={item.id} />
                            <tr>
                              <th>
                                <div className="text-left">
                                  <Text className="mt-xs">
                                    {item.description}
                                  </Text>
                                  {renderLineItemStatus(item)}
                                </div>
                              </th>
                              <td>
                                <div className="text-right">
                                  <Money
                                    format="DOLLARS_WITH_CENTS"
                                    value={Math.abs(item.remaining)}
                                  />
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                        {showCreditCardFee && (
                          <FeeRow
                            label={getFeeLabel(
                              selectedPaymentMethod.paymentMethod,
                            )}
                            value={getSignedFee(remaining)}
                          />
                        )}
                        <tr
                          className="h-2sm border-0 border-b border-dashed border-gray"
                          colSpan="2"
                        />
                        <tr className="h-2sm " colSpan="2" />
                        <tr>
                          <th>
                            <div className="text-left">
                              <Text fontWeight="semibold">Total</Text>
                            </div>
                          </th>
                          <td>
                            <div className="text-right">
                              <Money
                                format="DOLLARS_WITH_CENTS"
                                fontWeight="semibold"
                                value={getTotalWithFee(remaining)}
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {payNowFlowSection}
              </ModalLayout.Section>
            </ModalLayout.Content>

            {showSplitItBar && (
              <ModalLayout.BottomBar
                className="m-0 w-full md:px-sm"
                leftSideNode={
                  <SplitPaymentsBanner
                    maxCashBack={maxCashBack}
                    withPlanItButton={false}
                    showAutoSplitLabel={isAutosplittable}
                  />
                }
                rightNode={
                  <Button
                    variant="solid"
                    buttonType="primary"
                    onClick={navigateToSplitPayments}
                  >
                    <div className="flex items-center justify-center">
                      <Text variant="body" fontWeight="semibold">
                        {ACCOUNTS_EARNINGS['section.split.action']}
                      </Text>
                    </div>
                  </Button>
                }
                fixedBottom
              />
            )}
            {canPayNow && (
              <ModalLayout.BottomBar
                className="m-0 w-full md:px-sm"
                fixedBottom
                rightNode={
                  <Button
                    onClick={handlePayNow}
                    disabled={isPayNowDisabled || isSubmitting}
                  >
                    {PAY_NOW_STRINGS['pay_now.button']}
                  </Button>
                }
              />
            )}
            {!showSplitItBar && !canPayNow && (
              <ModalLayout.BottomBar
                className="m-0 w-full md:px-sm"
                noButton
                fixedBottom
              />
            )}
          </ModalLayout>
        </ModalV2>
      </>
    );
  }

  const balances = balance?.balances?.length > 0 ? balance?.balances : [];
  const payableBalances =
    balance?.balances?.length > 0
      ? balance?.balances?.filter((bal) => bal.balanceType === 'Payable')
      : [];

  return (
    <>
      <ModalV2
        {...rest}
        show={rest.show}
        noPadding
        showSpinner={false}
        onHide={handleHide}
        className=""
      >
        <ModalLayout fixedBottom>
          <ModalLayout.Content className="p-0">
            <ModalLayout.Section>
              <ModalLayout.Title className="md:px-sm">
                {balance?.description}
              </ModalLayout.Title>
              <ModalLayout.SubTitle noPadding>
                <Text className="text-navy">{home}</Text>
              </ModalLayout.SubTitle>
              <div className={`mt-xl ${canPayNow ? '' : 'pb-xl'}`}>
                <div className="flex md:px-sm">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th>
                          <div className="text-left">
                            <Text variant="h3" fontWeight="semibold">
                              Details
                            </Text>
                          </div>
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="h-sm" colSpan="2" />
                      {balances.length > 0
                        ? balances.map((bal) => {
                            if (bal.balanceType === 'Payable') return null;
                            return (
                              <tr key={bal.sourceKey}>
                                <th>
                                  <div className="text-left">
                                    <Text className="mt-xs">
                                      {bal.description}
                                    </Text>
                                    {renderLineItemStatus(bal)}
                                  </div>
                                </th>
                                <td>
                                  <div className="text-right">
                                    <Money
                                      format="DOLLARS_WITH_CENTS"
                                      value={bal.netBalance}
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        : null}
                      {/* {balances?.some((bal) => bal.balanceType !== 'Payable') && (
                        <>
                          <tr className="h-2sm border-0 border-b border-gray border-dashed" colSpan="2" />
                          <tr className="h-2sm " colSpan="2" />
                          <tr>
                            <td>
                              <div className="text-left">
                                <Text>Subtotal</Text>
                              </div>
                            </td>
                            <td>
                              <div className="text-right">
                                <Money format="DOLLARS_WITH_CENTS" value={balance.subTotalBalance} />
                              </div>
                            </td>
                          </tr>
                        </>
                      )} */}
                      {payableBalances.length > 0
                        ? payableBalances.map((payableBal) => {
                            return (
                              <tr key={payableBal.id}>
                                <th>
                                  <div className="text-left">
                                    <Text className="mt-xs">
                                      {payableBal.description}
                                    </Text>
                                    {renderLineItemStatus(payableBal)}
                                  </div>
                                </th>
                                <td>
                                  <div className="text-right">
                                    <Money
                                      format="DOLLARS_WITH_CENTS"
                                      value={payableBal.netBalance}
                                      fontWeight="semibold"
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        : null}

                      {showCreditCardFee && (
                        <FeeRow
                          label={getFeeLabel(
                            selectedPaymentMethod.paymentMethod,
                          )}
                          value={getSignedFee(balance?.netBalance)}
                        />
                      )}
                      <tr
                        className="h-2sm border-0 border-b border-dashed border-gray"
                        colSpan="2"
                      />
                      <tr className="h-2sm " colSpan="2" />
                      <tr>
                        <th>
                          <div className="text-left">
                            <Text fontWeight="semibold">Total</Text>
                          </div>
                        </th>
                        <td>
                          <div className="text-right">
                            <Money
                              format="DOLLARS_WITH_CENTS"
                              fontWeight="semibold"
                              value={getTotalWithFee(balance?.netBalance)}
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {payNowFlowSection}
            </ModalLayout.Section>
          </ModalLayout.Content>
          {showSplitItBar && (
            <ModalLayout.BottomBar
              className="m-0 w-full md:px-sm"
              noPadding
              leftSideNode={
                <SplitPaymentsBanner
                  maxCashBack={maxCashBack}
                  withPlanItButton={false}
                  showAutoSplitLabel={isAutosplittable}
                />
              }
              rightNode={
                <Button
                  variant="solid"
                  buttonType="primary"
                  onClick={navigateToSplitPayments}
                >
                  <div className="flex items-center justify-center">
                    <Text variant="body" fontWeight="semibold">
                      {ACCOUNTS_EARNINGS['section.split.action']}
                    </Text>
                  </div>
                </Button>
              }
              fixedBottom
            />
          )}
          {canPayNow && (
            <ModalLayout.BottomBar
              className="m-0 w-full md:px-sm"
              fixedBottom
              rightNode={
                <Button
                  onClick={handlePayNow}
                  disabled={isPayNowDisabled || isSubmitting}
                >
                  {PAY_NOW_STRINGS['pay_now.button']}
                </Button>
              }
            />
          )}
          {!showSplitItBar && !canPayNow && (
            <ModalLayout.BottomBar
              className="m-0 w-full md:px-sm"
              noButton
              fixedBottom
            />
          )}
        </ModalLayout>
      </ModalV2>
      {!isResident && splitPaymentModal && (
        <SplitPaymentModal
          show={splitPaymentModal}
          onHide={handleSplitPaymentsModal}
          loading={false}
          items={[balance]}
          amount={balance.netBalance}
          handleShowFinanceSuccessModal={onShowFinanceSuccessModal}
          billingInfo={selectedBalance.billingInfo}
        />
      )}
    </>
  );
}

BalanceDetailsModal.propTypes = {
  balance: PropTypes.object,
  onHide: PropTypes.func,
  standalone: PropTypes.bool,
  handleShowFinanceSuccessModal: PropTypes.func,
  balanceSummary: PropTypes.object,
  simulateSplitPayments: PropTypes.func,
  isResident: PropTypes.bool,
  showPayNow: PropTypes.bool,
  onPaymentComplete: PropTypes.func,
  paymentMethods: PropTypes.array,
  fetchPaymentMethods: PropTypes.func,
  createBillsPayment: PropTypes.func,
};

function mapStateProps(state) {
  return {
    simulation: selectSimulatePayments(state),
    paymentMethods: selectPaymentMethods(state),
  };
}
const mapDispatchToProps = {
  simulateSplitPayments,
  fetchPaymentMethods,
  createBillsPayment,
};

export default connect(mapStateProps, mapDispatchToProps)(BalanceDetailsModal);
